/**
 * Inlines the four SVG sprites once, at the root of the app.
 *
 * Chrome has never supported external references in `<use href="…">`, so the
 * sprite has to be in the document. The design system's preview fetches them at
 * runtime and says to inline them here instead — this is that.
 *
 * They are imported rather than copied, so a change to the design system is
 * picked up by a rebuild and cannot silently drift.
 */

import iconSprite from "../../../design/assets/icons.svg?raw";
import statusSprite from "../../../design/assets/status-shapes.svg?raw";
import statusIconSprite from "../../../design/assets/status-icons.svg?raw";
import logoSprite from "../../../design/assets/logo-sprite.svg?raw";

/**
 * Drops the XML prolog and leading comments.
 *
 * These files are standalone SVG documents; what goes into an HTML document is
 * the root element and nothing before it.
 */
function svgOnly(source: string): string {
  const start = source.indexOf("<svg");
  return start === -1 ? "" : source.slice(start);
}

// status-shapes carries the 8px chart markers, status-icons the 14px badge
// pictograms. Both are needed: see the header of status-icons.svg for why they
// are two vocabularies rather than one.
const markup =
  svgOnly(iconSprite) + svgOnly(statusSprite) + svgOnly(statusIconSprite) + svgOnly(logoSprite);

export function Sprites() {
  return (
    <div
      hidden
      aria-hidden="true"
      // Static asset content from our own repository, not user input.
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}

/** Every icon in the sprite. Named so a typo is a type error, not a blank box. */
export type IconName =
  | "search"
  | "close"
  | "copy"
  | "check"
  | "mail"
  | "sms"
  | "push"
  | "log"
  | "contacts"
  | "groups"
  | "campaign"
  | "template"
  | "form"
  | "dns"
  | "key"
  | "chevron"
  | "selector"
  | "filter"
  | "plus"
  | "external"
  | "settings"
  | "webhook"
  | "refresh"
  | "warning"
  | "chart"
  | "team"
  | "building"
  | "sidebar";

interface IconProps {
  name: IconName;
  className?: string;
  size?: number;
}

export function Icon({ name, className, size }: IconProps) {
  const style = size ? { width: size, height: size } : undefined;
  return (
    <svg className={className} style={style} aria-hidden="true" focusable="false">
      <use href={`#i-${name}`} />
    </svg>
  );
}
